import { useState } from 'react';
import { colour, space, text } from '../design/tokens.ts';
import { Card, Chip, Page, Select, Status, Thumb } from '../components/basics.tsx';
import { CouldNotLoad, Empty, Loading } from '../components/states.tsx';
import { api, query } from '../api/client.ts';
import { useLoad } from '../app/useLoad.ts';

/**
 * Screen 15: one timeline for one thing.
 *
 * The history of a VC pass lives in three collections with three shapes, and
 * somebody looking at a stale render does not care which. The server gathers
 * them and gives every entry the same shape; the counts are taken before
 * filtering, so clicking a tab does not make the other tabs read zero.
 */

interface Entry {
  kind: string;
  at: string;
  who: string;
  says: string;
  reason: string | null;
  thumbnail: string | null;
  at_frame?: { frame: number | null; timecode: string | null };
  pinned?: boolean;
}

interface Timeline {
  target: { type: string; id: string; says: string };
  entries: Entry[];
  counts: Record<string, number>;
  people: string[];
  labels: Record<string, string>;
  where_this_stands: {
    good: { kind: string; version: number; says: string; thumbnail: string | null }[];
    owners: string[];
    stale_reasons: string[];
    state: string | null;
    says: string;
  };
}

interface TakeRow {
  _id: string;
  scene?: string;
  shot?: string;
  take?: string;
  conductor_take_id: string;
  capture_date?: string;
}

export function TimelineScreen({
  target,
  id,
  projectId,
  onOpen
}: {
  target: string;
  id: string;
  projectId: string | null;
  onOpen: (target: string, id: string) => void;
}) {
  const [filter, setFilter] = useState('everything');
  const [person, setPerson] = useState('');

  // A timeline is always *of* something, and "Notes and history" in the menu
  // arrives with nothing chosen. Asking the server for the history of ""
  // only ever produced a 400, so pick first and ask afterwards.
  const chosen = id !== '';

  const { state, reload } = useLoad<Timeline>(
    () =>
      chosen
        ? api.get(`/api/timeline/${target}/${id}${query({ filter, person })}`)
        : Promise.resolve(null as unknown as Timeline),
    [target, id, chosen, filter, person]
  );
  const timeline = chosen ? state.data : null;

  return (
    <Page
      eyebrow="MAESTRO · NOTES AND HISTORY"
      title={timeline?.target.says ?? 'History'}
      description="Everything that has happened, in one list: notes, status changes, runs and files moved."
    >
      {!chosen && <PickSomething projectId={projectId} onOpen={onOpen} />}

      {chosen && state.status === 'loading' && !timeline && (
        <Loading says="Loading the history…" rows={6} />
      )}
      {chosen && state.status === 'failed' && !timeline && (
        <CouldNotLoad what="this history" error={state.error} onRetry={reload} />
      )}

      {timeline && (
        <>
          <Card title="Where this stands" aside={timeline.where_this_stands.says}>
            {timeline.where_this_stands.state && <Status value={timeline.where_this_stands.state} />}
            {timeline.where_this_stands.good.map((good) => (
              <div key={`${good.kind}-${good.version}`} style={{ display: 'flex', gap: space.tight, alignItems: 'center' }}>
                <Thumb url={good.thumbnail} alt={`${good.kind} v${good.version}`} size={64} />
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={text.smallStrong}>
                    {good.kind} · version {good.version}
                  </span>
                  <span style={{ ...text.mono, color: colour.inkSoft }}>{good.says}</span>
                </span>
              </div>
            ))}
            {timeline.where_this_stands.owners.length > 0 && (
              <span style={{ ...text.note, color: colour.inkMuted }}>
                owned by {timeline.where_this_stands.owners.join(', ')}
              </span>
            )}
            {timeline.where_this_stands.stale_reasons.map((reason) => (
              <span key={reason} style={{ ...text.note, color: colour.staleInk }}>
                {reason}
              </span>
            ))}
          </Card>

          <Card>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: space.tight }}>
              {Object.entries(timeline.labels).map(([key, says]) => (
                <Chip key={key} on={filter === key} onClick={() => setFilter(key)}>
                  {says} {timeline.counts[key] ?? 0}
                </Chip>
              ))}
              <span style={{ flex: 1 }} />
              <Select
                value={person}
                onChange={setPerson}
                options={[
                  { value: '', label: 'Anybody' },
                  ...timeline.people.map((p) => ({ value: p, label: p }))
                ]}
              />
            </div>
          </Card>

          {timeline.entries.length === 0 ? (
            <Empty title="Nothing here" says="No entries of that kind, by that person." />
          ) : (
            <Card>
              {timeline.entries.map((entry, i) => (
                <div
                  key={`${entry.at}-${i}`}
                  style={{
                    display: 'flex',
                    gap: space.control,
                    paddingTop: space.tight,
                    borderTop: i === 0 ? 'none' : `1px solid ${colour.edgeSoft}`
                  }}
                >
                  {entry.thumbnail && <Thumb url={entry.thumbnail} alt="" size={64} />}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    <span style={text.small}>{entry.says}</span>
                    {entry.reason && (
                      <span style={{ ...text.note, color: colour.inkSoft }}>{entry.reason}</span>
                    )}
                    <span style={{ ...text.note, color: colour.inkMuted }}>
                      {entry.who} · {new Date(entry.at).toLocaleString()}
                      {entry.at_frame?.timecode ? ` · at ${entry.at_frame.timecode}` : ''}
                    </span>
                  </div>
                </div>
              ))}
            </Card>
          )}
        </>
      )}
    </Page>
  );
}

/**
 * What to show when the menu brought somebody here without a subject.
 *
 * A timeline is of a take, a VC pass or a splat; there is no such thing as
 * "the history" on its own. Nothing else in the app links here, so this is
 * the only way in, and it has to be a way in rather than an error.
 */
function PickSomething({
  projectId,
  onOpen
}: {
  projectId: string | null;
  onOpen: (target: string, id: string) => void;
}) {
  const { state, reload } = useLoad<{ takes: TakeRow[]; total: number }>(
    () =>
      projectId
        ? api.get(`/api/projects/${projectId}/takes`)
        : Promise.resolve({ takes: [], total: 0 }),
    [projectId]
  );

  const says = (take: TakeRow): string => {
    const parts = [take.scene && `Scene ${take.scene}`, take.shot && `Shot ${take.shot}`, take.take && `Take ${take.take}`].filter(Boolean);
    return parts.length > 0 ? parts.join(' · ') : take.conductor_take_id;
  };

  return (
    <Card
      title="Whose history?"
      aside={state.data ? `${state.data.total} ${state.data.total === 1 ? 'take' : 'takes'}` : undefined}
    >
      <span style={{ ...text.small, color: colour.inkSoft }}>
        A timeline is of one thing — a take, a VC pass or a splat. Pick a take to start, or open a
        pass from the board and come back through it.
      </span>

      {state.status === 'loading' && <Loading says="Loading the takes…" rows={3} />}
      {state.status === 'failed' && (
        <CouldNotLoad what="the takes" error={state.error} onRetry={reload} />
      )}

      {(state.data?.takes ?? []).map((take) => (
        <button
          key={take._id}
          type="button"
          onClick={() => onOpen('take', take._id)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 2,
            minHeight: 44,
            padding: '8px 12px',
            border: `1px solid ${colour.edge}`,
            borderRadius: 8,
            background: colour.surface,
            color: colour.ink,
            font: text.body.font,
            cursor: 'pointer',
            textAlign: 'left'
          }}
        >
          <span style={text.bodyStrong}>{says(take)}</span>
          <span style={{ ...text.note, color: colour.inkMuted }}>{take.conductor_take_id}</span>
        </button>
      ))}

      {state.status === 'ready' && (state.data?.takes ?? []).length === 0 && (
        <Empty
          title="This project has no takes yet"
          says="A take arrives from Conductor, and its history starts the moment something is made from it."
        />
      )}
    </Card>
  );
}
